import React, { useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";

// components
import Button from "../../../../../components/Button";
import Pagination from "../../../../../components/Pagination";
import ReviewTemplateModal from "../../../../../components/ReviewTemplateModal";
import { SideNavigationLink } from "../../../../../components/SideNavigation";
import Spacer from "../../../../../components/Spacer";
import ScrollableTable from "../../../../../components/TableLite/TableLiteScrollable";
import AllReviewsFilters, {
	AllReviewsFilters as AllReviewsFiltersType,
	StatusSelectionOptions,
} from "./components/AllReviewsFilters";
import AllReviewsReadableFiltersList from "./components/AllReviewsReadableFiltersList";
import getAllReviewsTableCols from "./components/AllReviewsTableCols";

// hooks
import { SortDirection, useDirectReports, useSearchService, useSearchServicePrefetch } from "../../../../../api/hooks";

//utils
import { SERVICE_REVIEW } from "../../../../../api/services/defs/search-service";
import { Review } from "../../../../../api/services/searchServiceResultTypes/reviews";

import { getUserID } from "../../../../../selectors/app";

import { getFiltersForSearchService } from "./utils";

//templates
import InfoPageTemplate from "../../../../../pageTemplates/InfoPageTemplate";
import EmptySearchTableMessage from "../../../../../pageTemplates/InfoPageTemplate/EmptySearchTableMessage";
import StandardToolbarActionRow from "../../../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
//constants
import { CompanyReviewsNavigationLinks, REVIEWS_TABLE_LIMIT } from "../../constants";

type AllReviewsProps = {
	navLinks: SideNavigationLink[];
};

const AllReviews = ({ navLinks }: AllReviewsProps) => {
	const navigate = useNavigate();
	const { page } = useParams<{ page: string }>();
	const userID = useSelector((state) => getUserID(state));
	const { data: directReportData } = useDirectReports(userID);

	const [isFiltersPanelOpen, setIsFiltersPanelOpen] = useState(false);

	const [isNewReviewModalOpen, setIsNewReviewModalOpen] = useState<boolean>(false);
	const [activeReviewTemplate, setActiveReviewTemplate] = useState<string | null>(null);

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<AllReviewsFiltersType>({
		defaultSortName: "start_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
		fallbackFilters: {
			status: [StatusSelectionOptions[0].value, StatusSelectionOptions[2].value],
		},
	});

	const handleTemplateInputChange = (value: string | null) => {
		setActiveReviewTemplate(value);
	};

	const createReviewURL = activeReviewTemplate
		? `/start-review/${activeReviewTemplate}`
		: "/start-review/?subject=null";

	const { data, isLoading } = useSearchService<Review>({
		service: SERVICE_REVIEW,
		filters: getFiltersForSearchService(filters, debouncedSearchValue, userID, directReportData),
		skip: (pageNumber - 1) * REVIEWS_TABLE_LIMIT,
		limit: REVIEWS_TABLE_LIMIT,
		sort,
		defaultErrorMessage: "There was an error getting Reviews",
	});

	//prefetch the review templates 1 page before and 3 after

	useSearchServicePrefetch<Review>(
		{
			service: SERVICE_REVIEW,
			filters: getFiltersForSearchService(filters, debouncedSearchValue, userID, directReportData),
			skip: (pageNumber - 2) * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
		},
		pageNumber <= 1 ? 0 : 1
	);

	useSearchServicePrefetch<Review>(
		{
			service: SERVICE_REVIEW,
			filters: getFiltersForSearchService(filters, debouncedSearchValue, userID, directReportData),
			skip: pageNumber * REVIEWS_TABLE_LIMIT,
			limit: REVIEWS_TABLE_LIMIT,
			sort,
		},
		3
	);

	const reviews = data?.results || [];
	const totalReviews = data?.total || 0;

	const emptyTableState = <EmptySearchTableMessage mainText="No Reviews Found" />;

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={(value) => setDebouncedSearch(value)}
			onFilterButtonPress={() => {
				setIsFiltersPanelOpen(true);
			}}
			activeFiltersCount={Object.keys(filters).length}
			searchPlaceholder="Search reviews..."
		/>
	);

	const toolbarInformationRow = <AllReviewsReadableFiltersList filters={filters} setFilters={setFilters} />;

	const actionButtons = [
		<Button color="green" key="create-review-cycle-button" href="/review-cycle/create/">
			New Review Cycle
		</Button>,
		<Button color="green" onClick={() => setIsNewReviewModalOpen(true)} key="create-review-button">
			New Review
		</Button>,
	];

	const drawer = (
		<AllReviewsFilters
			filters={filters}
			isOpen={isFiltersPanelOpen}
			onClose={() => setIsFiltersPanelOpen(false)}
			onApplyFilters={(newFilters) => setFilters(newFilters)}
		/>
	);

	const mainContent = (
		<div>
			<ScrollableTable
				columns={getAllReviewsTableCols()}
				rows={reviews}
				isLoading={isLoading}
				activeSort={sort.name}
				activeSortDirection={sort.direction}
				onColumnClick={(columnID, nextDirection) =>
					setSort({ name: columnID, direction: nextDirection as SortDirection })
				}
				emptyChildren={emptyTableState}
			/>
			<Spacer height={2} />
			<Pagination
				pageCount={Math.ceil(totalReviews / REVIEWS_TABLE_LIMIT)}
				currentPage={pageNumber}
				onClick={(page: number) => setPageNumber(page)}
			/>
		</div>
	);

	return (
		<div>
			<InfoPageTemplate
				toolbarActionRow={toolbarActionRow}
				toolbarInformationRow={toolbarInformationRow}
				actionButtons={actionButtons}
				mainContent={mainContent}
				drawer={drawer}
				navigationLinks={navLinks || CompanyReviewsNavigationLinks}
				activeNavigationLinkKey={page}
			/>
			{isNewReviewModalOpen ? (
				<ReviewTemplateModal
					handleTemplateInputChange={handleTemplateInputChange}
					handleModalCancel={() => setIsNewReviewModalOpen(false)}
					createReview={() => {
						navigate(createReviewURL);
					}}
				/>
			) : null}
		</div>
	);
};

export default AllReviews;
